/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soonki Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Mar 17 2023
 **/

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AllHTMLAttributes, createElement, forwardRef } from "react";

import { filterHTMLAttribute } from "@/frameworks/libraries/utilities";

import {
	createStyled,
	domElementList,
	breakPoint,
	createBreakPoint,
	keys as breakPointKeys
} from "@/frameworks/designStuff/system";

type GridProps = {
	column?: { [size in breakPointKeys]?: number } | number;
	row?: { [size in breakPointKeys]?: number } | number;
	repeatSize?: { column?: UnitString; row?: UnitString };
	gridGap?:
		| number
		| string
		| {
				row?: number | string;
				column?: number | string;
		  };
};
type GridBaseType = React.ForwardRefExoticComponent<
	React.ComponentProps<typeof StyledGrid> & React.RefAttributes<unknown>
>;
type GridTagsType = {
	[tag in keyof JSX.IntrinsicElements]: GridBaseType;
};

interface CreateGrid extends GridBaseType, GridTagsType {}

const GridCss = (props?: GridProps) => css`
	display: grid;
	grid-template-columns: ${(() => {
		if (!props?.column) return null;
		if (typeof props.column === "number") return `repeat(${props?.column}, ${props.repeatSize?.column || "1fr"})`;
		return `repeat(${props?.column.xl}, ${props.repeatSize?.column || "1fr"})`;
	})()};
	grid-template-rows: ${(() => {
		if (!props?.row) return null;
		if (typeof props.row === "number") return `repeat(${props?.row}, ${props.repeatSize?.row || "1fr"})`;
		return `repeat(${props?.row.xl}, ${props.repeatSize?.row || "1fr"})`;
	})()};
	grid-row-gap: ${(() => {
		if (!props?.gridGap) return null;
		if (typeof props.gridGap === "number") return `${props.gridGap}px`;
		if (typeof props.gridGap === "string") return `${props.gridGap}`;

		if (!props.gridGap.row) return null;
		if (typeof props.gridGap.row === "number") return `${props.gridGap.row}px`;
		if (typeof props.gridGap.row === "string") return `${props.gridGap.row}`;
	})()};
	grid-column-gap: ${(() => {
		if (!props?.gridGap) return null;
		if (typeof props.gridGap === "number") return `${props.gridGap}px`;
		if (typeof props.gridGap === "string") return `${props.gridGap}`;

		if (!props.gridGap.column) return null;
		if (typeof props.gridGap.column === "number") return `${props.gridGap.column}px`;
		if (typeof props.gridGap.column === "string") return `${props.gridGap.column}`;
	})()};

	${createBreakPoint(breakPoint.xl).breakPointDown} {
		grid-template-columns: ${(() => {
			if (!props?.column) return null;
			if (typeof props.column === "number")
				return `repeat(${props?.column}, ${props.repeatSize?.column || "1fr"})`;
			return `repeat(${props?.column.xl}, ${props.repeatSize?.column || "1fr"})`;
		})()};
		grid-template-rows: ${(() => {
			if (!props?.row) return null;
			if (typeof props.row === "number") return `repeat(${props?.row}, ${props.repeatSize?.row || "1fr"})`;
			return `repeat(${props?.row.xl}, ${props.repeatSize?.row || "1fr"})`;
		})()};
	}

	${createBreakPoint(breakPoint.l).breakPointDown} {
		grid-template-columns: ${(() => {
			if (!props?.column) return null;
			if (typeof props.column === "number")
				return `repeat(${props?.column}, ${props.repeatSize?.column || "1fr"})`;
			return `repeat(${props?.column.l}, ${props.repeatSize?.column || "1fr"})`;
		})()};
		grid-template-rows: ${(() => {
			if (!props?.row) return null;
			if (typeof props.row === "number") return `repeat(${props?.row}, ${props.repeatSize?.row || "1fr"})`;
			return `repeat(${props?.row.l}, ${props.repeatSize?.row || "1fr"})`;
		})()};
	}

	${createBreakPoint(breakPoint.m).breakPointDown} {
		grid-template-columns: ${(() => {
			if (!props?.column) return null;
			if (typeof props.column === "number")
				return `repeat(${props?.column}, ${props.repeatSize?.column || "1fr"})`;
			return `repeat(${props?.column.m}, ${props.repeatSize?.column || "1fr"})`;
		})()};
		grid-template-rows: ${(() => {
			if (!props?.row) return null;
			if (typeof props.row === "number") return `repeat(${props?.row}, ${props.repeatSize?.row || "1fr"})`;
			return `repeat(${props?.row.m}, ${props.repeatSize?.row || "1fr"})`;
		})()};
	}

	${createBreakPoint(breakPoint.s).breakPointDown} {
		grid-template-columns: ${(() => {
			if (!props?.column) return null;
			if (typeof props.column === "number")
				return `repeat(${props?.column}, ${props.repeatSize?.column || "1fr"})`;
			return `repeat(${props?.column.s}, ${props.repeatSize?.column || "1fr"})`;
		})()};
		grid-template-rows: ${(() => {
			if (!props?.row) return null;
			if (typeof props.row === "number") return `repeat(${props?.row}, ${props.repeatSize?.row || "1fr"})`;
			return `repeat(${props?.row.s}, ${props.repeatSize?.row || "1fr"})`;
		})()};
	}

	${createBreakPoint(breakPoint.xs).breakPointDown} {
		grid-template-columns: ${(() => {
			if (!props?.column) return null;
			if (typeof props.column === "number")
				return `repeat(${props?.column}, ${props.repeatSize?.column || "1fr"})`;
			return `repeat(${props?.column.xs}, ${props.repeatSize?.column || "1fr"})`;
		})()};
		grid-template-rows: ${(() => {
			if (!props?.row) return null;
			if (typeof props.row === "number") return `repeat(${props?.row}, ${props.repeatSize?.row || "1fr"})`;
			return `repeat(${props?.row.xs}, ${props.repeatSize?.row || "1fr"})`;
		})()};
	}
`;

const StyledGrid = styled(
	forwardRef((props: { tag?: keyof JSX.IntrinsicElements } & GridProps & AllHTMLAttributes<HTMLElement>, ref) => {
		const { tag = "div", ...rest } = props;

		const htmlAttrs = filterHTMLAttribute(rest);

		return createElement(tag, { ...htmlAttrs, ref });
	})
)`
	${(props) => GridCss(props)}
`;

const GridBase: GridBaseType = forwardRef(({ ...rest }: React.ComponentProps<typeof StyledGrid>, ref) =>
	createStyled(StyledGrid)({ ...rest, ref })
);

/**
 * @param tag HTML 태그명 ex) div | form | ul
 * @param column 그리드 레이아웃 행의 개수. (xs, s, m, l, xl은 반응형 분기입니다.)
 * @param row 그리드 레이아웃 열의 개수. (xs, s, m, l, xl은 반응형 분기점입니다.)
 * @param gridGap 그리드 레이아웃 자식들간의 간격
 */
const Grid = GridBase as CreateGrid;

domElementList.forEach((domElement) => {
	Grid[domElement] = forwardRef(({ ...rest }, ref) =>
		createStyled(StyledGrid, domElement)[domElement]({ ...rest, ref })
	);
});

export default Grid;
